import { useState, useEffect } from 'react';
import { getOrdersStatusMatrix } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { Grid3x3 } from 'lucide-react';

interface StatusMatrixData {
  financial_statuses: string[];
  fulfillment_statuses: string[];
  matrix: {
    [financialStatus: string]: {
      [fulfillmentStatus: string]: {
        order_count: number;
        revenue: number;
      };
    };
  };
  financial_totals: { [key: string]: number };
  fulfillment_totals: { [key: string]: number };
  grand_total: number;
}

const StatusMatrixReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StatusMatrixData | null>(null);

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      toastMessage('Please select both dates', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await getOrdersStatusMatrix({
        from_date: fromDate,
        to_date: toDate,
      });

      if (response.success) {
        setData(response.data as StatusMatrixData);
      } else {
        toastMessage('Failed to fetch report', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Error fetching report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const getStatusColor = (status: string) => {
    if (status.includes('paid')) return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400';
    if (status.includes('pending')) return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400';
    if (status.includes('refund')) return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400';
    if (status.includes('fulfil')) return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4">
          {/* Left Content */}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Grid3x3 className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Status Matrix</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Financial × Fulfillment cross-tabulation</p>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={fetchReport}
              disabled={loading}
              className="px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Explanation */}
        <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <svg className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-0.5">About this report</p>
            <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
              Cross-tabulation of <strong>financial status</strong> (paid / pending / refunded / partially paid) vs <strong>fulfillment status</strong> (fulfilled / unfulfilled / partial).
              Each cell shows the order count and revenue for that combination. Use this to flag problematic combinations — e.g. <em>paid but unfulfilled</em> orders that need immediate attention,
              or <em>refunded + fulfilled</em> orders that may indicate returns.
            </p>
          </div>
        </div>
        {data && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.grand_total}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Financial Statuses</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.financial_statuses.length}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Fulfillment Statuses</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.fulfillment_statuses.length}</p>
              </div>
            </div>

            {/* Matrix Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order Distribution</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Financial Status</th>
                      {data.fulfillment_statuses.map((status) => (
                        <th key={status} className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">
                          {status}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.financial_statuses.map((financialStatus) => (
                      <tr key={financialStatus} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-4 font-medium text-gray-900 dark:text-gray-100">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(financialStatus)}`}>
                            {financialStatus}
                          </span>
                        </td>
                        {data.fulfillment_statuses.map((fulfillmentStatus) => {
                          const cellData = data.matrix[financialStatus]?.[fulfillmentStatus];
                          return (
                            <td key={`${financialStatus}-${fulfillmentStatus}`} className="px-4 py-4 text-center">
                              {cellData ? (
                                <div className="space-y-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {cellData.order_count}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatCurrency(cellData.revenue)}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-600">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-4 text-right font-medium text-gray-900 dark:text-gray-100">
                          {data.financial_totals[financialStatus]}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-300 dark:border-gray-600">
                      <td className="px-4 py-4 font-semibold text-gray-900 dark:text-gray-100">Total</td>
                      {data.fulfillment_statuses.map((status) => (
                        <td key={`total-${status}`} className="px-4 py-4 text-center font-semibold text-gray-900 dark:text-gray-100">
                          {data.fulfillment_totals[status]}
                        </td>
                      ))}
                      <td className="px-4 py-4 text-right font-bold text-lg text-gray-900 dark:text-gray-100">
                        {data.grand_total}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {loading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading report...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusMatrixReport;
